import React from "react"
import styled from "styled-components"
import {
	Flex,
	Text,
	Icon,
	IconTypes,
	scale,
	colors,
	breakpoint,
} from "@capioweb/ui-components/src"
import { ContentAreaProperty, XhtmlString } from "../../@types/CapioWeb/core"
import { StringProperty } from "../../@types/EpiFoundationSpa/Property"
import { PageBaseProps } from "../../components/Core/pageBase"
import { CommonHeroBlock, BreadcrumbsBlock } from "../../components"
import { BlockCommonLayout } from "../../utils/layout"

interface DiagnosisPageLink {
	name: string
	url: string
}

interface DiagnosisListingPageLink {
	id: string
	icon: IconTypes
	name: string
	url: string
	children: DiagnosisPageLink[]
}

interface CapioNexusDiagnosisListingPageProps extends PageBaseProps {
	mainContent: ContentAreaProperty
	header: StringProperty
	preamble: XhtmlString
	children: DiagnosisListingPageLink[]
}

const ColumnGrid = styled.nav`
	display: flex;
	flex-direction: column;
	width: 100%;

	${breakpoint("m")`
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		column-gap: ${scale.px(3)};
	`}
`

interface ColumnGridItemProps {
	columnNo: number
}

const ColumnGridItem = styled.div<ColumnGridItemProps>`
	grid-column: ${(props) => props.columnNo || 1};
`

const RoundedIcon = styled.span`
	position: relative;
	width: ${scale.px(4.5)};
	height: ${scale.px(4.5)};
	background-color: ${colors.purple33};
	border: 1px solid ${colors.purple66};
	margin-top: ${scale.px(-1.5)};
	border-radius: 50%;
	overflow: hidden;

	svg {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translateY(-50%) translateX(-50%);
	}
`

const CapioNexusDiagnosisListingPage: React.FC<CapioNexusDiagnosisListingPageProps> =
	({ children, ...rest }) => {
		const [contentCols, setContentCols] = React.useState(null)

		React.useEffect(() => {
			if (children?.length > 0) {
				const items = children.slice()
				const threeSplitIndex = Math.round(children.length / 3)
				let cols = null

				cols =
					children.length > 2
						? (cols = [
							[...items.splice(-threeSplitIndex)],
							[...items.splice(-threeSplitIndex)],
							[...items],
						].reverse())
						: (cols = [
							[...items.slice(0, 1)],
							[...items.slice(1, items.length)],
						])

				setContentCols(cols)
			}
		}, [setContentCols, children])

		return (
			<Flex display="block" w={1}>
				<BreadcrumbsBlock items={rest.breadcrumb} />
				<CommonHeroBlock heading={rest.header} preamble={rest.preamble} />
				<BlockCommonLayout>
					<ColumnGrid>
						{contentCols &&
							contentCols.map((col, i) => (
								<ColumnGridItem columnNo={i + 1} key={i}>
									{col.map((link) => (
										<Flex key={link.id} flexDirection="column" mb={3}>
											<Flex alignItems="center">
												<RoundedIcon>
													<Icon icon={link.icon} size={4} />
												</RoundedIcon>
												<Text.Link
													to={link?.url ?? "#"}
													fontSize={2.5}
													lineHeight={3}
													fontWeight="bold"
													mb={1.5}
													ml={1}
													underline
												>
													{link.name}
												</Text.Link>
											</Flex>
											<Flex as="ul" w={1} flexDirection="column">
												{link.children.map((child, i) => (
													<Flex as="li" key={i + child.name}>
														<Text.Link
															to={child?.url ?? "#"}
															lineHeight={3}
															mb={1.5}
															ml={5.5}
															underline
														>
															{child.name}
														</Text.Link>
													</Flex>
												))}
											</Flex>
										</Flex>
									))}
								</ColumnGridItem>
							))}
					</ColumnGrid>
				</BlockCommonLayout>
			</Flex>
		)
	}

export { CapioNexusDiagnosisListingPage }
